// src/MaintenanceProtectedRoute.js
//
// Gate for the /maintenance hub.
//   • Unauthenticated visitors are sent to /login, with their intended URL
//     stashed in sessionStorage so LoginPage can bounce them back after sign-in.
//   • Authenticated users whose role !== 'maintenance' see an explicit "no
//     access" message rather than being silently redirected — this keeps the
//     hub strictly internal and makes mis-assigned accounts obvious.
//
// The "maintenance" role is expected to be set on the user record in the
// auth backend (same place that already sets 'corporate', 'regional', etc.).

import React, { useEffect, useRef } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
// See note in MaintenanceHome.js — use the shared config so production
// builds never pick up the local-dev REACT_APP_API_URL=localhost:5051.
import API_BASE_URL from './config/api';
import { isPreviewMode } from './previewSession';
import { hasAnyMaintenanceAccess, categoriesForRole } from './maintenanceAccess';
import { MaintenanceAccessProvider, MaintenanceTabGate, useMaintenanceAccess, MAINTENANCE_ACCESS_CHANGED } from './MaintenanceAccessContext';

function readUser() {
  const token = localStorage.getItem('managerToken');
  const userStr = localStorage.getItem('managerUser');
  if (!token || !userStr) return null;
  try {
    return JSON.parse(userStr);
  } catch {
    return null;
  }
}

// Decode the JWT's payload (just the middle segment, no signature check —
// purely so we can spot a missing `role` claim and trigger a refresh).
function decodeJwtPayload(token) {
  try {
    const seg = token.split('.')[1];
    if (!seg) return null;
    const base = seg.replace(/-/g, '+').replace(/_/g, '/');
    const json = atob(base + '==='.slice((base.length + 3) % 4));
    return JSON.parse(json);
  } catch {
    return null;
  }
}

// Per-route gate: wrap a child route so that visiting it directly via
// URL bounces a user without the required category back to /maintenance.
// (The sidebar already hides the link; this is the URL-typing safety
// net.) Designed to live INSIDE MaintenanceProtectedRoute so we can
// assume `user` exists and skip the login redirect dance.
export function CategoryGate({ category, children }) {
  const { role } = useMaintenanceAccess();
  const allowed = categoriesForRole(role);
  if (allowed.has(category)) return children;
  return <Navigate to="/maintenance" replace />;
}

export default function MaintenanceProtectedRoute({ children }) {
  const location = useLocation();
  const navigate = useNavigate();
  const user = readUser();
  // Captured up-front (before any hooks) so we can return a redirect
  // AFTER all hooks have been called below — required by the rules of
  // hooks. If this tab is in "preview as manager" mode, any visit to
  // /maintenance is almost certainly an accidental sidebar click on the
  // patched managerUser; bounce back to the previewed manager dashboard
  // so the preview session stays coherent.
  const inPreview = isPreviewMode();

  // One-shot: if the current bearer token was minted before we started
  // embedding `role` in the JWT (or somehow lost the claim), silently call
  // /units/refresh-token so admin-only endpoints stop 403-ing on us.
  const refreshAttempted = useRef(false);
  useEffect(() => {
    if (refreshAttempted.current) return;
    const token = localStorage.getItem('managerToken');
    if (!token) return;
    const payload = decodeJwtPayload(token);
    if (!payload) return;
    if (payload.role) return; // already has role claim, nothing to do
    refreshAttempted.current = true;
    (async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/units/refresh-token`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        });
        if (!res.ok) {
          console.warn('[MaintenanceProtectedRoute] refresh-token failed', res.status);
          return;
        }
        const data = await res.json();
        if (data?.token) {
          localStorage.setItem('managerToken', data.token);
          if (data.user) {
            localStorage.setItem('managerUser', JSON.stringify(data.user));
          }
          // Trigger a soft reload of the current route so downstream
          // components pick up the new token. navigate() with replace=true
          // re-renders without adding to history.
          window.dispatchEvent(new Event(MAINTENANCE_ACCESS_CHANGED));
          navigate(location.pathname + location.search, { replace: true });
        }
      } catch (e) {
        console.warn('[MaintenanceProtectedRoute] refresh-token error', e);
      }
    })();
  }, [navigate, location.pathname, location.search]);

  // Post-hooks: handle preview mode FIRST so a maintenance user who's
  // mid-preview never gets bounced to /login by the !user branch below
  // (in preview tabs the synthetic user has role 'manager', not
  // 'maintenance', so the role-gate check would otherwise show the
  // "no access" panel).
  if (inPreview) {
    return <Navigate to="/" replace />;
  }

  if (!user) {
    const intended = location.pathname + location.search;
    sessionStorage.setItem('redirectAfterLogin', intended);
    return <Navigate to="/login" replace />;
  }

  // Any maintenance-* tier (L1/L2/L3) or the legacy `maintenance` role
  // grants entry to the hub itself. Per-category filtering happens
  // inside MaintenanceHub (sidebar) and the CategoryGate route wrapper.
  if (!hasAnyMaintenanceAccess(user.role)) {
    return (
      <div
        style={{
          minHeight: '100vh',
          background: '#F4F7FB',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '2rem',
          fontFamily:
            '-apple-system, BlinkMacSystemFont, "Segoe UI", "Inter", "Helvetica Neue", Arial, sans-serif',
        }}
      >
        <div
          style={{
            background: '#ffffff',
            border: '1px solid #E6E8EC',
            borderRadius: 14,
            padding: '32px 36px',
            maxWidth: 460,
            width: '100%',
            boxShadow: '0 1px 3px rgba(20,30,60,.06), 0 8px 24px rgba(20,30,60,.04)',
            textAlign: 'center',
          }}
        >
          <div
            style={{
              fontSize: 13,
              color: '#dc2626',
              fontWeight: 700,
              textTransform: 'uppercase',
              letterSpacing: '.08em',
              marginBottom: 8,
            }}
          >
            Restricted
          </div>
          <h1
            style={{
              fontSize: 22,
              fontWeight: 700,
              color: '#111827',
              margin: '0 0 10px',
              letterSpacing: '-0.01em',
            }}
          >
            Maintenance Hub Only
          </h1>
          <p style={{ fontSize: 14, color: '#6b7280', lineHeight: 1.55, margin: '0 0 18px' }}>
            Your account ({user.username || user.email || 'no username'}) does not have access to
            this internal page. Ask an admin to set your role to{' '}
            <code
              style={{
                background: '#f3f4f6',
                padding: '1px 6px',
                borderRadius: 4,
                fontSize: 12.5,
                color: '#1F3A68',
                fontWeight: 600,
              }}
            >
              Employee
            </code>
            ,{' '}
            <code
              style={{
                background: '#f3f4f6',
                padding: '1px 6px',
                borderRadius: 4,
                fontSize: 12.5,
                color: '#1F3A68',
                fontWeight: 600,
              }}
            >
              Manager
            </code>
            , or{' '}
            <code
              style={{
                background: '#f3f4f6',
                padding: '1px 6px',
                borderRadius: 4,
                fontSize: 12.5,
                color: '#1F3A68',
                fontWeight: 600,
              }}
            >
              Admin
            </code>{' '}
            if you should be here.
          </p>
          <div style={{ display: 'flex', gap: 10, justifyContent: 'center' }}>
            <button
              type="button"
              onClick={() => navigate('/', { replace: true })}
              style={{
                background: '#1F3A68',
                color: '#fff',
                border: 'none',
                borderRadius: 8,
                padding: '9px 16px',
                fontSize: 13,
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              Go to my dashboard
            </button>
            <button
              type="button"
              onClick={() => {
                localStorage.removeItem('managerToken');
                localStorage.removeItem('managerUser');
                navigate('/login', { replace: true });
              }}
              style={{
                background: '#ffffff',
                color: '#374151',
                border: '1px solid #d1d5db',
                borderRadius: 8,
                padding: '9px 16px',
                fontSize: 13,
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              Sign out
            </button>
          </div>
        </div>
      </div>
    );
  }

  return <MaintenanceAccessProvider><MaintenanceTabGate>{children}</MaintenanceTabGate></MaintenanceAccessProvider>;
}
